import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "clonecast_theme";
const MODES = ["light", "dark", "system"];

export const getStoredMode = () => {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return MODES.includes(value) ? value : "dark";
  } catch {
    // private mode / blocked storage — fall back to the baseline theme
    return "dark";
  }
};

const systemPrefersDark = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-color-scheme: dark)").matches;

export const resolveTheme = (mode) =>
  mode === "system" ? (systemPrefersDark() ? "dark" : "light") : mode;

/** Stamp the resolved theme on <html>; the CSS keys off data-theme. */
export const applyTheme = (mode) => {
  const resolved = resolveTheme(mode);
  document.documentElement.setAttribute("data-theme", resolved);
  return resolved;
};

export function useTheme() {
  const [mode, setMode] = useState(getStoredMode);
  const [resolved, setResolved] = useState(() => resolveTheme(getStoredMode()));

  useEffect(() => {
    setResolved(applyTheme(mode));
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // preference just won't persist; the UI still switches
    }
  }, [mode]);

  // follow the OS while the user is on "system"
  useEffect(() => {
    if (mode !== "system" || !window.matchMedia) return undefined;
    const query = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setResolved(applyTheme("system"));
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, [mode]);

  const cycle = useCallback(() => {
    setMode((current) => MODES[(MODES.indexOf(current) + 1) % MODES.length]);
  }, []);

  return { mode, setMode, resolved, cycle, modes: MODES };
}
